import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { FileText, Plus, ArrowRight, Calendar, RefreshCw, Mail, Building2, CheckCircle, Clock, XCircle, DollarSign } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CIDashboard() {
  var { user, token } = useAuth();
  var [stats, setStats] = useState(null);
  var [loading, setLoading] = useState(true);

  useEffect(function() {
    axios.get(API_URL + '/api/ci-dashboard/stats?authorization=' + token)
      .then(function(r) { setStats(r.data); })
      .catch(function() { toast.error('Failed to load C&I dashboard'); })
      .finally(function() { setLoading(false); });
  }, [token]);

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-4 border-[#005394] border-t-transparent"></div></div></Layout>;
  }

  var recent = (stats && stats.recent_contracts) || [];

  function statusBadge(s) {
    if (s === 'won') return <Badge className="bg-green-100 text-green-800 text-xs">Won</Badge>;
    if (s === 'sent') return <Badge className="bg-blue-100 text-blue-800 text-xs">Sent</Badge>;
    if (s === 'lost') return <Badge className="bg-red-100 text-red-800 text-xs">Lost</Badge>;
    return <Badge className="bg-slate-100 text-slate-600 text-xs">Draft</Badge>;
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="ci-dashboard">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">C&I Dashboard</h1>
            <p className="text-slate-600 mt-1">Commercial & Industrial contracts overview</p>
          </div>
          <Link to="/contracts">
            <Button className="rounded-full bg-[#005394] hover:bg-[#005394]/90 gap-2" data-testid="new-ci-contract-btn">
              <Plus className="w-5 h-5" />New C&I Contract
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-l-4 border-l-[#005394] shadow-sm hover:shadow-md transition-shadow" data-testid="stat-total-contracts">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div><p className="text-sm font-medium text-slate-500">Total Contracts</p><p className="text-3xl font-bold text-slate-900 mt-1">{stats ? stats.total_contracts : 0}</p></div>
                <div className="w-12 h-12 rounded-full bg-[#005394]/10 flex items-center justify-center"><FileText className="w-6 h-6 text-[#005394]" /></div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500 shadow-sm hover:shadow-md transition-shadow" data-testid="stat-won">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div><p className="text-sm font-medium text-slate-500">Won</p><p className="text-3xl font-bold text-green-600 mt-1">{stats ? stats.won_count : 0}</p></div>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center"><CheckCircle className="w-6 h-6 text-green-600" /></div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500 shadow-sm hover:shadow-md transition-shadow" data-testid="stat-draft">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div><p className="text-sm font-medium text-slate-500">Draft / Pending</p><p className="text-3xl font-bold text-amber-600 mt-1">{stats ? stats.draft_count + stats.sent_count : 0}</p></div>
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center"><Clock className="w-6 h-6 text-amber-600" /></div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-slate-400 shadow-sm hover:shadow-md transition-shadow" data-testid="stat-renewals">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div><p className="text-sm font-medium text-slate-500">Renewal Customers</p><p className="text-3xl font-bold text-slate-900 mt-1">{stats ? stats.total_renewal_groups : 0}</p></div>
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center"><RefreshCw className="w-6 h-6 text-slate-500" /></div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions + Recent Contracts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="shadow-sm">
            <CardHeader><CardTitle className="text-lg">Quick Actions</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Link to="/contracts" className="block">
                <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors group">
                  <div className="w-10 h-10 rounded-full bg-[#005394]/10 flex items-center justify-center group-hover:bg-[#005394]/20 transition-colors"><Plus className="w-5 h-5 text-[#005394]" /></div>
                  <div className="flex-1"><p className="font-medium text-slate-900">New C&I Contract</p><p className="text-sm text-slate-500">Create a new contract from scratch</p></div>
                  <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-[#005394] transition-colors" />
                </div>
              </Link>
              <Link to="/email-to-quote" className="block">
                <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors group">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center group-hover:bg-purple-200 transition-colors"><Mail className="w-5 h-5 text-purple-600" /></div>
                  <div className="flex-1"><p className="font-medium text-slate-900">Email to C&I Quote</p><p className="text-sm text-slate-500">Paste an email to auto-generate contract</p></div>
                  <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-purple-600 transition-colors" />
                </div>
              </Link>
              <Link to="/renewals" className="block">
                <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors group">
                  <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center group-hover:bg-teal-200 transition-colors"><RefreshCw className="w-5 h-5 text-teal-600" /></div>
                  <div className="flex-1"><p className="font-medium text-slate-900">Automated Renewals</p><p className="text-sm text-slate-500">Bulk generate renewal contracts</p></div>
                  <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-teal-600 transition-colors" />
                </div>
              </Link>
            </CardContent>
          </Card>

          <Card className="lg:col-span-2 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Recent Contracts</CardTitle>
              <Link to="/contracts"><Button variant="ghost" size="sm" className="text-[#005394]">View All</Button></Link>
            </CardHeader>
            <CardContent>
              {recent.length > 0 ? (
                <div className="space-y-3">
                  {recent.map(function(c) {
                    return (
                      <Link key={c.id} to="/contracts" state={{ editContractId: c.id }} className="block">
                        <div className="flex items-center gap-4 p-4 rounded-lg border border-slate-200 hover:border-[#005394]/30 hover:shadow-sm transition-all">
                          <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center"><Building2 className="w-6 h-6 text-slate-500" /></div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-900 truncate">{c.company_name || 'Untitled'}</p>
                              <Badge variant="outline" className="text-xs font-mono">{c.agreement_number}</Badge>
                              {statusBadge(c.status)}
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                              <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{new Date(c.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                              {c.contract_state && <Badge variant="outline" className="text-xs">{c.contract_state}</Badge>}
                              {c.commission_type && c.commission_value && (
                                <span className="flex items-center gap-1 text-emerald-600 font-medium"><DollarSign className="w-3.5 h-3.5" />{c.commission_value}{c.commission_type === 'percent' ? '%' : ' c/kWh'}</span>
                              )}
                            </div>
                          </div>
                          <ArrowRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No C&I contracts yet</p>
                  <Link to="/contracts"><Button className="mt-4 rounded-full bg-[#005394] hover:bg-[#005394]/90" data-testid="empty-new-ci-btn">Create Your First Contract</Button></Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Pipeline Summary */}
        <Card className="shadow-sm">
          <CardHeader><CardTitle className="text-lg">Pipeline Summary</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center p-4 rounded-lg bg-slate-50">
                <p className="text-2xl font-bold text-slate-700">{stats ? stats.draft_count : 0}</p>
                <p className="text-xs text-slate-500 mt-1">Draft</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-blue-50">
                <p className="text-2xl font-bold text-blue-700">{stats ? stats.sent_count : 0}</p>
                <p className="text-xs text-blue-600 mt-1">Sent</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-green-50">
                <p className="text-2xl font-bold text-green-700">{stats ? stats.won_count : 0}</p>
                <p className="text-xs text-green-600 mt-1">Won</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-red-50">
                <p className="text-2xl font-bold text-red-700">{stats ? stats.lost_count : 0}</p>
                <p className="text-xs text-red-600 mt-1">Lost</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-emerald-50">
                <p className="text-2xl font-bold text-emerald-700">{stats ? stats.with_commission : 0}</p>
                <p className="text-xs text-emerald-600 mt-1">With Commission</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
